import { DurationFormat } from '@se/se.ui.html5';
import { BoolEditor, DateFormatEnum, DateInputEnum, MatColor } from '@se/ui.datagrid';
import { ColDef } from 'ag-grid-community';
import { DataBindingPropertyType, DataBindingType } from './abstractions';

export interface IConfigControlBinding {
    dataBinding: DataBindingType;
}

export interface IConfigControlSimple extends IConfigControlBinding {
    readOnly: boolean;
    required: boolean;
}

export interface IConfigControlWithTooltip extends IConfigControlSimple {
    icon?: string | null;
    tooltip?: string | null;
}

export interface IConfigControlNumeric extends IConfigControlWithTooltip {
    min?: number | null;
    max?: number | null;
}

export interface IDataSourceWhere {
    readonly type: 'where';
    propertyName: string;
    value: any;
}

export type IDataSourceQuery = IDataSourceWhere;

export interface IConfigControlWithDataSource extends IConfigControlWithTooltip {
    isDynamicDataSource?: boolean;
    idDataSource: string;
    queries?: IDataSourceQuery[];
    isWithLabelOnTop?: boolean | null;
}

export interface IConfigControlDuration extends IConfigControlWithTooltip {
    format: DurationFormat;
}

export interface IConfigControlTextEditor extends IConfigControlSimple {
    isWithLabelInToolbar?: boolean | null;
    suggestions?: string | null; // DGS: attributo della configurazione per usare le suggestions recuperate da datasource
    toggleDataBinding?: DataBindingType | null;
    toolbar: 'light' | 'full';
    enableDefaultText?: boolean; // TODO  inseritio '?' per fare checkin. CP-Editor togliere '?' quando siete pronti
    defaultTextGroupName?: string | null;
    defaultTextName?: string | null;
}

export interface IConfigColumnGridControl extends IConfigControlWithDataSource {
    controlType: IDataGridControlType;
    desiredSize: number;
    order: number;
    sortable?: boolean;
    sort?: IConfigColumnSort;
    title: string;
    visibilityStatus: IVisibilityStatus;
    controlParameters: IControlParameters;
    formatterRendererType?: IDataGridFormatterRendererType;
    formatterRendererParameters: IFormatterRendererParameters;
}

export interface IConfigColumnSort {
    default: IGridColumnSortDefault; //none//descending
    operator: string; // chiave che individua la funzione da usare come comparatore ById, ByName….
}

export interface IConfigControlGrid {
    columnWidthUnitMeasure?: IColumnWidthUnitMeasure;
    readonly?: boolean;
    inlineEditor?: boolean;
    selectionType?: IGridSelectionType;
    singleClickEdit?: boolean;
    columnConfigArray: IConfigColumnGridControl[];
    customColumnsConfig?: ColDef[];
    disableSort?: boolean;
}

export interface IConfigControlRadio extends IConfigControlWithTooltip {
    order: number;
    title: string;
    default?: boolean;
}

export interface IConfigControlLabel {
    italic: boolean;
    bold: boolean;
    underlined: boolean;
    url?: string;
}

export interface IConfigControlImage {
    id: string;
    tooltip?: string | null;
}

export interface IConfigControlAnatomicalMap extends IConfigControlSimple {
    imageId: string;
    mapId: string;
}

export interface IControlIcon {
    iconTooltip?: string;
    icon?: string;
}

export interface IConfigControlBaseButton extends IControlIcon {
    color?: string;
}

export interface IConfigControlConstraintButton extends IConfigControlBaseButton {
    idConstraints: string[];
}

export interface IConfigControlCustomButton extends IConfigControlBaseButton {
    idUserControl: string;
    parameters: Record<string, string | number | boolean>;
}

export interface IConfigControlPanelButton extends IConfigControlBaseButton {
    idPanel: string;
    idConstraints?: string[];
}

export interface IConfigControlServerButton extends IConfigControlBaseButton {
    endpoint: string;
}

export interface IConfigControlGridDefaultGroups extends IConfigControlWithTooltip {
    idGroupList: string;
    idGroupItemList: string;
}

export type IConfigControlButton =
    | IConfigControlConstraintButton
    | IConfigControlCustomButton
    | IConfigControlPanelButton
    | IConfigControlServerButton;

export type UserControlParameters = Record<string, string | number | boolean>;

export interface IConfigControlUserControl {
    id: string;
    parameters: UserControlParameters;
}

export type GraphAxisType = 'number' | 'date';

export interface GraphAxisConfig {
    prop: string;
    type: GraphAxisType;
}

export interface IConfigControlGraph {
    dataBinding: string;
    x: GraphAxisConfig;
    y: GraphAxisConfig;
}

export interface IConfigControlImport extends IConfigControlBinding {
    gridHeader?: string;
    gridVisibleProp?: string;
    dataBindingTarget?: DataBindingType;
}

export interface IConfigControlMeasure extends IConfigControlWithTooltip {
    additionalInformation?: string;
    conversionFactor?: number | DataBindingPropertyType; //default 1
    decimalDigits: number | DataBindingPropertyType;
    detailsEnabled: boolean;
    formulaDescription?: string | DataBindingPropertyType; //'IdUsMeasure.FormulaDescription'
    formulaReference?: string | DataBindingPropertyType;
    maxNormalValue: number | DataBindingPropertyType | null;
    minNormalValue: number | DataBindingPropertyType | null; //'MinNormalValue'
    normalityReferenceValue: number | DataBindingPropertyType | null;
    unitOfMeasureDisplayed?: string | DataBindingPropertyType;
    shownInfo?: string | DataBindingPropertyType;
    method?: string | DataBindingPropertyType;
    mode?: string | DataBindingPropertyType;
    idPanel?: string;
    description?: string | DataBindingPropertyType;
    acquisitionDate?: string | DataBindingPropertyType;
}

export interface IConfigControlMapContainer extends IConfigControlSimple {
    legendDataSource: string;
    segmentsDataSource: string;
    segmentLabelType?: SegmentLabels;
    isHorizontal?: boolean;
    legendActionsConfiguration?: LegendButtons[];
    isWithTooltip?: boolean;
    isWithViewId?: boolean;
    fixedViewsColNumber?: number;
    viewTopLabel?: string;
    viewTopLabelBinding?: DataBindingType;
    viewBottomLabel?: string;
    viewBottomLabelBinding?: DataBindingType;
    viewsImageBinding: DataBindingType;
    legendImageBinding: DataBindingType;
    viewSetBinding?: DataBindingType;
    viewsSets?: IViewSets;
    isToPrintBinding?: DataBindingType;
}

//ereditato: binding Stage[]
export interface IConfigControlMultiStageMapContainer extends IConfigControlSimple {
    stageSegments: DataBindingPropertyType; //LVKinetic
    stageNameProperty: DataBindingPropertyType; //DicomDescription
    stageOrderProperty: DataBindingPropertyType; //DicomOrder
    stageIsToPrint?: DataBindingPropertyType; //IsToPrint
    legendDataSource: string;
    segmentsDataSource: string;
    segmentLabelType?: SegmentLabels;
    isHorizontal?: boolean;
    legendActionsConfiguration?: AnatomicMapButtons[];
    isWithTooltip?: boolean;
    isWithStageName?: boolean;
    isWithViewId?: boolean;
    fixedViewsColNumber?: number;
    viewTopLabel?: string | DataBindingPropertyType;
    viewBottomLabel?: string | DataBindingPropertyType;
    stageViewImageBinding?: DataBindingPropertyType;
    legendImageBinding?: DataBindingType;
    viewSetBinding?: DataBindingType;
    viewsSets?: IViewSets;
    selectOnly?: boolean;
    viewBinding: DataBindingType; // ecostress views array
    viewIdProperty?: DataBindingPropertyType; // IdUsStressView object into the view // da non gestire nel configuratore
    layoutMode?: MultistageMapLayoutMode; // da non gestire nel configuratore
}

export interface IViewSets {
    default: string[];

    [key: string]: string[];
}

export enum MultistageMapLayoutMode {
    VIEWSET_PER_STAGE = 'ViewSetPerStage',
    DYNAMIC_LAYOUT = 'DynamicLayout',
}

export type IConfigControl =
    | IConfigControlAnatomicalMap
    | IConfigControlButton
    | IConfigControlWithDataSource
    | IConfigControlDuration
    | IConfigControlGrid
    | IConfigControlImage
    | IConfigControlLabel
    | IConfigControlMapContainer
    | IConfigControlMultiStageMapContainer
    | IConfigControlNumeric
    | IConfigControlRadio
    | IConfigControlSimple
    | IConfigControlBinding
    | IConfigControlTextEditor
    | IConfigControlImport
    | IConfigControlUserControl
    | IConfigControlMeasure
    | IConfigControlGridDefaultGroups;

export enum IControlType {
    UserControl = 1,
    Selector = 2,
    TextSingleLine = 4,
    TextMultiLine = 5,
    TextFormatted = 6,
    NumericInteger = 7,
    NumericDecimal = 8,
    DateTime = 9,
    Date = 10,
    Duration = 11,
    Time = 12,
    Money = 13,
    Measure = 14,
    DataGrid = 15,
    Combo = 17,
    ComboMultiSelector = 18,
    Label = 19,
    CheckBox = 21,
    ThreeStateCheckBox = 22,
    HorizontalRadio = 23,
    VerticalRadio = 24,
    ComboWithFilter = 25,
    ComboMultiSelectorWithFilter = 26,
    AnatomicalMap = 40,
    MapContainer = 41,
    MultiStageMapContainer = 42,
    Image = 50,
    Import = 55,
    CustomButton = 60,
    ServerButton = 61,
    Autocomplete = 62,
    ConstraintButton = 63,
    PanelButton = 64,
    Spacer = 70,
    Graph = 80,
    DataGridNoAddDelete = 90,
    DataGridDefaultGroups = 91,
    // Deprecated
    DataGridWithAddDelete = 16,
}

export enum IDataGridControlType {
    AlphanumericCellEditor = 151,
    BooleanCellRenderer = 152,
    ComboCellEditor = 153,
    DateCellEditor = 154,
    LetterCellEditor = 155,
    NumericCellEditor = 156,
    MaskedDateCellEditor = 157,
    AutocompleteCellEditor = 158,
}

export enum IDataGridFormatterRendererType {}

export enum IColumnWidthUnitMeasure {
    PX = 'PX',
    PERC = '*',
}

export enum IGridSelectionType {
    SINGLE = 'SINGLE',
    MULTIPLE = 'MULTIPLE',
    NONE = 'NONE',
}

export enum IVisibilityStatus {
    HIDE = 0,
    VISIBLE = 1,
}

export interface IBaseCellEditorParameters {
    editInline?: boolean;
}

export interface IAlphanumericCellEditorParameters extends IBaseCellEditorParameters {
    notAdmissibleChars?: Array<string>;
}

export interface IDataSourceCellEditorParameters extends IBaseCellEditorParameters {
    idDataSource: string;
}

export interface IComboCellEditorParameters extends IDataSourceCellEditorParameters {
    viewValueFieldName?: string;
    valueFieldName?: string;
    multi?: boolean;
    isResettable?: boolean;
}

export interface IDateCellEditorParameters extends IBaseCellEditorParameters {
    inputType?: DateInputEnum;
    inputFormat?: DateFormatEnum;
}

export interface INumericCellEditorParameters extends IBaseCellEditorParameters {
    max?: number;
    min?: number;
    decimal?: number;
}

export interface IBooleanCellRendererParameters extends IBaseCellEditorParameters {
    color?: MatColor;
    editor?: BoolEditor;
}

export enum MultiIconCellRendererIconType {
    AngularMaterial = 0,
    SeSvg = 1,
    SeFont = 2,
}

export interface IMultiIconCellRendererConfig {
    icon: string;
    negate: boolean;
    tooltip?: string;
    iconsize?: string;
    iconType?: MultiIconCellRendererIconType;
    field: string;
    emptySpace?: boolean;
}

export interface IIconCellRendererParameters extends IBaseCellEditorParameters {
    estensa: IMultiIconCellRendererConfig[];
}

export type IControlParameters =
    | IAlphanumericCellEditorParameters
    | IComboCellEditorParameters
    | IDateCellEditorParameters
    | IBooleanCellRendererParameters
    | INumericCellEditorParameters
    | IDataSourceCellEditorParameters
    | IIconCellRendererParameters;

export interface IDateCelleRendererParameters {
    inputType?: DateInputEnum;
    inputFormat?: DateFormatEnum;
}

export type IFormatterRendererParameters = IDateCelleRendererParameters;

export enum IGridColumnSortDefault {
    ASC = 'ASC',
    DESC = 'DESC',
    NONE = 'NONE',
}

export enum LegendButtons {
    colorAllBtn = 'colorAllBtn',
    notEvaluatedAllBtn = 'notEvaluatedAllBtn',
    colorSelectedBtn = 'colorSelectedBtn',
    notEvaluatedSelectedBtn = 'notEvaluatedSelectedBtn',
}

export enum MultistageMapButtons {
    copyFromPrevious = 'copyFromPrevious',
}

export const AnatomicMapButtonsMerged = { ...LegendButtons, ...MultistageMapButtons } as const;

export type AnatomicMapButtons = LegendButtons | MultistageMapButtons;

export enum SegmentLabels {
    none = 'none',
    // valore numerico 'as-id' recuperato da vista
    value = 'value',
    // descrizione sintetica recuperata da vista
    label = 'label',
    // valore numerico associato al colore
    alphaCode = 'alphaCode',
}
